import {
  cloneElement,
  ReactElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import {useTranslation} from 'react-i18next'

import {CloseIcon, RenderIf, SearchIcon} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {DopamineAssistantMark} from './DopamineAssistantMark'
import {DopamineTableNavigationContext} from './DopamineAssistantTable.context'
import {
  DopamineTableData,
  getDopamineTableNumber,
} from './DopamineAssistantTable.utils'

type DopamineAssistantTableDialogProps = {
  data: DopamineTableData
  downloadButton: ReactElement<{className: string}>
  downloadFeedback: ReactElement
  onClose(): void
  title: string
}

const FOCUSABLE_ELEMENTS =
  'button:not(:disabled), a[href], input:not(:disabled), [tabindex="0"]'

export const DopamineAssistantTableDialog = ({
  data,
  downloadButton,
  downloadFeedback,
  onClose,
  title,
}: DopamineAssistantTableDialogProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const titleId = useId()
  const descriptionId = useId()
  const overlayRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const restoreFocusRef = useRef(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    const trigger = document.activeElement
    const previousOverflow = document.body.style.overflow
    const background = Array.from(document.body.children).filter(
      element => element !== overlayRef.current,
    )
    const previousHidden = background.map(element =>
      element.getAttribute('aria-hidden'),
    )
    background.forEach(element => element.setAttribute('aria-hidden', 'true'))
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus({preventScroll: true})
    const containFocus = (event: FocusEvent): void => {
      if (!dialogRef.current?.contains(event.target as Node))
        closeRef.current?.focus({preventScroll: true})
    }
    document.addEventListener('focusin', containFocus)
    return () => {
      document.removeEventListener('focusin', containFocus)
      document.body.style.overflow = previousOverflow
      background.forEach((element, index) => {
        const previous = previousHidden[index]
        if (previous === null) element.removeAttribute('aria-hidden')
        else element.setAttribute('aria-hidden', previous)
      })
      if (
        restoreFocusRef.current &&
        trigger instanceof HTMLElement &&
        trigger.isConnected
      ) {
        trigger.focus({preventScroll: true})
      }
    }
  }, [])

  const rows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    return data.rows.filter(
      row =>
        !query ||
        row.cells.some(cell => cell.text.toLocaleLowerCase().includes(query)),
    )
  }, [data.rows, search])

  const numericColumns = data.headers.map(
    (_, index) =>
      data.rows.length > 0 &&
      data.rows.every(
        row =>
          getDopamineTableNumber(row.cells[index]?.text ?? '') !== undefined,
      ),
  )

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/[0.62] p-4 md:p-12"
    >
      <dialog
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="static m-0 flex max-h-full max-w-full flex-col overflow-hidden rounded-2xl border-0.5 border-white/10 bg-[#121216] p-0 text-[#f4f4f6] shadow-2xl"
        ref={dialogRef}
        open
        style={{
          width: Math.min(1120, Math.max(640, data.headers.length * 160)),
        }}
        onKeyDown={event => {
          // The Assistant also handles Escape and shortcuts at document level.
          event.stopPropagation()
          if (event.key === KEY_CODES.ESCAPE) {
            event.preventDefault()
            onClose()
          }
          if (event.key !== KEY_CODES.TAB) return
          const focusable = Array.from(
            dialogRef.current?.querySelectorAll<HTMLElement>(
              FOCUSABLE_ELEMENTS,
            ) ?? [],
          )
          const first = focusable[0]
          const last = focusable.at(-1)
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last?.focus()
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first?.focus()
          }
        }}
      >
        <div className="flex shrink-0 items-center gap-3 border-b-0.5 border-white/10 px-5 py-4">
          <span className="dopamine-mark-chip flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white shadow-inset-subtle">
            <DopamineAssistantMark className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="m-0 truncate text-sm font-semibold leading-5"
              title={title}
            >
              {title}
            </h2>
            <p
              id={descriptionId}
              className="mb-0 mt-1 text-xs text-dopamine-muted"
            >
              {t('DOPAMINE_TABLE_DIMENSIONS', {
                rows: data.rows.length,
                columns: data.headers.length,
              })}
            </p>
          </div>
          <div className="dopamine-header-actions flex shrink-0 gap-1">
            {cloneElement(downloadButton, {
              className: `${downloadButton.props.className} !h-7 !w-7`,
            })}
            <button
              aria-label={t('CLOSE')}
              className="flex h-7 w-7 items-center justify-center rounded text-dopamine-muted hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-dopamine-accent"
              onClick={onClose}
              ref={closeRef}
              type="button"
            >
              <CloseIcon height={16} width={16} strokeColor="currentColor" />
            </button>
          </div>
        </div>
        {downloadFeedback}
        <RenderIf isTrue={data.rows.length > 10}>
          <label className="mx-5 my-3 flex shrink-0 items-center gap-2 rounded-md border-0.5 border-white/10 bg-white/3 px-3 py-2 text-dopamine-muted">
            <SearchIcon height={14} width={14} strokeColor="currentColor" />
            <input
              aria-label={t('DOPAMINE_SEARCH_TABLE')}
              className="min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-dopamine-muted"
              onChange={event => setSearch(event.target.value)}
              placeholder={t('DOPAMINE_SEARCH_TABLE')}
              type="search"
              value={search}
            />
          </label>
        </RenderIf>
        <div className="min-h-0 overflow-auto">
          <DopamineTableNavigationContext.Provider
            value={() => {
              restoreFocusRef.current = false
              onClose()
            }}
          >
            <table className="w-full border-separate border-spacing-0 text-13px leading-5 [&_a]:text-dopamine-accent [&_a]:underline">
              <thead className="sticky top-0 z-10 bg-[#1a1c22]">
                <tr>
                  {data.headers.map((header, index) => (
                    <th
                      key={index}
                      scope="col"
                      className="whitespace-nowrap border-b-0.5 border-white/10 px-3.5 py-2.5 text-11px font-semibold uppercase tracking-wide text-[#c7c7c7]"
                      style={{
                        textAlign:
                          header.align ||
                          (numericColumns[index] ? 'right' : 'left'),
                      }}
                    >
                      {header.text}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id} className="hover:bg-white/3">
                    {row.cells.map((cell, index) => (
                      <td
                        key={index}
                        className="border-b-0.5 border-white/5 px-3.5 py-2.5 text-white/75 [overflow-wrap:anywhere] first:font-semibold first:text-white"
                        style={{
                          textAlign:
                            cell.align ||
                            (numericColumns[index] ? 'right' : 'left'),
                          fontVariantNumeric: numericColumns[index]
                            ? 'tabular-nums'
                            : undefined,
                        }}
                      >
                        {cell.content}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </DopamineTableNavigationContext.Provider>
          <RenderIf isTrue={rows.length === 0}>
            <output className="block p-5 text-center text-sm text-dopamine-muted">
              {t('DOPAMINE_TABLE_NO_RESULTS')}
            </output>
          </RenderIf>
        </div>
      </dialog>
    </div>,
    document.body,
  )
}
